import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography, type SxProps, type Theme } from "@mui/material";
import type { MatchingContentBlock, MatchingPairResult } from "../../types";
import { QuestionHeader } from "./question-header";
import { useInteractiveSubmit } from "../../hooks/useInteractiveSubmit";
import {
  interactiveRootSx,
  matchingColumnSx,
  matchingContainerSx,
  matchingItemTextSx,
  useInteractiveStyles,
} from "./styles";
import { useShuffledItems } from "../../hooks/useShuffledItems";
import type { AssessmentInteractiveModeProps } from "./assessment-mode";
import { notifyOfflineActionBlocked } from "../../utils/internetConnection";

interface MatchingBlockProps extends AssessmentInteractiveModeProps {
  block: MatchingContentBlock;
  blockIndex: number;
  messageId: string;
  userId: string;
  tabId: string;
  onQuizAnswered?: () => void;
}

type LineCoords = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  leftId: string;
};
const EMPTY_PAIRS: MatchingPairResult[] = [];
type MatchingAnswersShape = Record<string, string> | undefined;

const normalize = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

export const MatchingBlock: React.FC<MatchingBlockProps> = ({
  block,
  blockIndex,
  messageId,
  userId,
  tabId,
  onQuizAnswered,
  hideSubmitButton = false,
  hideFeedback = false,
  hideResultHighlight = false,
  isAssessmentMode = false,
  submitGetterRef,
  onCompletionChange,
}) => {
  const interactiveStyles = useInteractiveStyles(isAssessmentMode);
  const { palette } = interactiveStyles;
  const disabled = block.state === "disable";
  const revealResults = disabled && !hideResultHighlight;
  const containerRef = useRef<HTMLDivElement>(null);
  const leftRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const rightRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const { submit, isSubmitting, hasSubmitted, isBrowserLocked, isOffline } =
    useInteractiveSubmit(onQuizAnswered);
  const isInteractionLocked =
    isSubmitting || hasSubmitted || isBrowserLocked || isOffline;

  const [draftPairs, setDraftPairs] = useState<MatchingPairResult[]>(
    () => block.user_pairs ?? [],
  );

  useEffect(() => {
    setDraftPairs(block.user_pairs ?? []);
  }, [block.user_pairs, block.state, tabId]);
  const [selectedLeftId, setSelectedLeftId] = useState<string | null>(null);
  const [lineCoords, setLineCoords] = useState<LineCoords[]>([]);

  const pairs = useMemo(() => {
    if (disabled) {
      return block.user_pairs ?? EMPTY_PAIRS;
    }
    return draftPairs;
  }, [disabled, block.user_pairs, draftPairs]);

  const rightByLeft = useMemo(() => {
    const map = new Map<string, string>();
    pairs.forEach((p) => map.set(p.left_id, p.right_id));
    return map;
  }, [pairs]);

  const shuffledLeft = useShuffledItems(
    block.left,
    messageId,
    blockIndex,
    tabId,
    "left",
  );
  const shuffledRight = useShuffledItems(
    block.right,
    messageId,
    blockIndex,
    tabId,
    "right",
  );

  // Keep the same shuffled layout after submit so connection lines don't jump/cross.
  const displayLeft = shuffledLeft;
  const displayRight = shuffledRight;

  const usedRightIds = useMemo(
    () => new Set(pairs.map((p) => p.right_id)),
    [pairs],
  );
  const leftById = useMemo(() => new Map(block.left.map((item) => [item.id, item])), [block.left]);
  const rightById = useMemo(() => new Map(block.right.map((item) => [item.id, item])), [block.right]);
  const answers: MatchingAnswersShape = block.answers;

  const answerMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!answers || typeof answers !== "object") return map;
    Object.entries(answers).forEach(([leftId, rightId]) => {
      map.set(normalize(leftId), normalize(rightId));
    });
    return map;
  }, [answers]);

  const pairCorrectMap = useMemo(() => {
    const map = new Map<string, boolean>();
    pairs.forEach((pair) => {
      if (!revealResults || answerMap.size === 0) return;
      const left = leftById.get(pair.left_id);
      const right = rightById.get(pair.right_id);
      const expectedRight =
        answerMap.get(normalize(pair.left_id)) ?? answerMap.get(normalize(left?.text));
      if (!expectedRight) return;
      const isCorrect =
        normalize(pair.right_id) === expectedRight ||
        normalize(right?.text) === expectedRight;
      map.set(pair.left_id, isCorrect);
    });
    return map;
  }, [pairs, revealResults, answerMap, leftById, rightById]);

  const allMatched =
    block.left.length > 0 &&
    block.left.every((item) => rightByLeft.has(item.id));
  const isSubmitDisabled = !allMatched || isInteractionLocked;

  useEffect(() => {
    onCompletionChange?.(allMatched);
  }, [allMatched, onCompletionChange]);

  useEffect(() => {
    if (!submitGetterRef) return;
    submitGetterRef.current = () => {
      if (!allMatched) return null;
      return {
        interactionType: "matching",
        payload: {
          user_pairs: pairs.map(({ left_id, right_id }) => ({
            left_id,
            right_id,
          })),
        },
      };
    };
    return () => {
      submitGetterRef.current = null;
    };
  }, [submitGetterRef, allMatched, pairs]);

  const updateLines = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const next: LineCoords[] = [];

    pairs.forEach((pair) => {
      const leftEl = leftRefs.current[pair.left_id];
      const rightEl = rightRefs.current[pair.right_id];
      if (!leftEl || !rightEl) return;
      const leftRect = leftEl.getBoundingClientRect();
      const rightRect = rightEl.getBoundingClientRect();
      next.push({
        x1: leftRect.right - containerRect.left,
        y1: leftRect.top + leftRect.height / 2 - containerRect.top,
        x2: rightRect.left - containerRect.left,
        y2: rightRect.top + rightRect.height / 2 - containerRect.top,
        leftId: pair.left_id,
      });
    });
    setLineCoords(next);
  }, [pairs]);

  useEffect(() => {
    updateLines();
    const onResize = () => updateLines();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [updateLines, pairs, disabled]);

  const connectPair = useCallback(
    (leftId: string, rightId: string) => {
      if (disabled || hasSubmitted || isBrowserLocked) return;
      if (isOffline) {
        notifyOfflineActionBlocked();
        return;
      }
      setDraftPairs((prev) => {
        const filtered = prev.filter(
          (p) => p.left_id !== leftId && p.right_id !== rightId,
        );
        const existing = prev.find(
          (p) => p.left_id === leftId && p.right_id === rightId,
        );
        if (existing) return filtered;
        return [...filtered, { left_id: leftId, right_id: rightId }];
      });
      setSelectedLeftId(null);
    },
    [disabled, hasSubmitted, isBrowserLocked, isOffline],
  );

  const handleLeftClick = (leftId: string) => {
    if (disabled || hasSubmitted || isBrowserLocked) return;
    if (isOffline) {
      notifyOfflineActionBlocked();
      return;
    }
    setSelectedLeftId((prev) => (prev === leftId ? null : leftId));
  };

  const handleRightClick = (rightId: string) => {
    if (disabled || hasSubmitted || isBrowserLocked) return;
    if (isOffline) {
      notifyOfflineActionBlocked();
      return;
    }
    if (selectedLeftId) {
      connectPair(selectedLeftId, rightId);
    }
  };

  const getLeftHighlight = (
    leftId: string,
  ): "correct" | "incorrect" | "selected" | undefined => {
    if (!disabled) {
      return selectedLeftId === leftId ? "selected" : undefined;
    }
    if (!revealResults) return undefined;
    const isCorrect = pairCorrectMap.get(leftId);
    if (typeof isCorrect === "boolean") {
      return isCorrect ? "correct" : "incorrect";
    }
    if (block.is_correct === true && rightByLeft.has(leftId)) {
      return "correct";
    }
    if (block.is_correct === false && rightByLeft.has(leftId)) {
      return "incorrect";
    }
    return undefined;
  };

  const getRightHighlight = (
    rightId: string,
  ): "correct" | "incorrect" | undefined => {
    if (!revealResults) return undefined;
    const leftId = pairs.find((pair) => pair.right_id === rightId)?.left_id;
    if (!leftId) return undefined;
    const isCorrect = pairCorrectMap.get(leftId);
    if (typeof isCorrect === "boolean") {
      return isCorrect ? "correct" : "incorrect";
    }
    if (block.is_correct === true) {
      return "correct";
    }
    if (block.is_correct === false) {
      return "incorrect";
    }
    return undefined;
  };

  const getLineColor = (leftId: string) => {
    if (!revealResults) return palette.purpleBorder;
    const isCorrect = pairCorrectMap.get(leftId);
    if (isCorrect === true) return palette.green;
    if (isCorrect === false) return palette.red;
    if (block.is_correct === true) return palette.green;
    if (block.is_correct === false) return palette.red;
    return palette.purpleBorder;
  };

  const handleSubmit = async () => {
    if (!allMatched || disabled || hideSubmitButton || isInteractionLocked) {
      return;
    }
    await submit({
      messageId,
      userId,
      tabId,
      interactionType: "matching",
      blockIndex,
      data: block.data,
      payload: {
        user_pairs: pairs.map(({ left_id, right_id }) => ({
          left_id,
          right_id,
        })),
      },
    });
  };

  return (
    <Box sx={interactiveRootSx}>
      <QuestionHeader
        title={block.title}
        instruction={block.instruction}
        styles={interactiveStyles}
      />

      <Box
        ref={containerRef}
        sx={[interactiveStyles.cardSx, matchingContainerSx] as SxProps<Theme>}
      >
        <svg
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          {lineCoords.map((line, i) => (
            <line
              key={i}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke={getLineColor(line.leftId)}
              strokeWidth={2}
              strokeDasharray="6 4"
            />
          ))}
        </svg>

        <Box sx={matchingColumnSx}>
          {displayLeft.map((item) => (
            <Box
              key={item.id}
              component="div"
              ref={(el: HTMLDivElement | null) => {
                leftRefs.current[item.id] = el;
              }}
              onClick={() => handleLeftClick(item.id)}
              sx={interactiveStyles.matchingColumnCardSx(
                "left",
                getLeftHighlight(item.id),
              )}
            >
              <Typography component="span" sx={matchingItemTextSx}>
                {item.text}
              </Typography>
              <Box sx={interactiveStyles.matchingDotSx("left")} />
            </Box>
          ))}
        </Box>

        <Box aria-hidden="true" />

        <Box sx={matchingColumnSx}>
          {displayRight.map((item) => {
            const isUsed = !disabled && usedRightIds.has(item.id);
            return (
              <Box
                key={item.id}
                component="div"
                ref={(el: HTMLDivElement | null) => {
                  rightRefs.current[item.id] = el;
                }}
                onClick={() => handleRightClick(item.id)}
                sx={{
                  ...interactiveStyles.matchingColumnCardSx(
                    "right",
                    getRightHighlight(item.id),
                  ),
                  opacity: isUsed && !disabled ? 0.55 : 1,
                }}
              >
                <Box sx={interactiveStyles.matchingDotSx("right")} />
                <Typography component="span" sx={matchingItemTextSx}>
                  {item.text}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {!disabled ? (
        <Typography sx={interactiveStyles.poolLabelSx}>
          Tap a term on the left, then tap its match on the right.
        </Typography>
      ) : null}

      {block.feedback && disabled && !hideFeedback ? (
        <Typography sx={{ fontSize: "0.875rem", color: "#374151" }}>
          {block.feedback}
        </Typography>
      ) : null}

      {!disabled && !hideSubmitButton ? (
        <Button
          variant="contained"
          disableElevation
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          sx={interactiveStyles.submitButtonSx(isSubmitDisabled)}
        >
          {isSubmitting ? "Submitting..." : "Submit Answer"}
        </Button>
      ) : null}
    </Box>
  );
};
